import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Globe, Key, Check, X, RefreshCw, Search, Eye, EyeOff,
  ExternalLink, ToggleLeft, ToggleRight, ChevronDown, ChevronUp,
  Music, Film, Utensils, Gamepad2, Heart, DollarSign, Plane,
  Monitor, ShoppingCart, Image, Zap, Star, Unlock, Lock,
  Play, Copy, AlertCircle, Activity, Coffee, Video,
  Upload, Link, Plus, Trash2, FileText, Brain
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  imagenes:      { label: "Imágenes & Media",      icon: <Image size={16} />,       color: "text-pink-400",   bg: "bg-pink-500/10" },
  videos:        { label: "Videos & Streaming",     icon: <Video size={16} />,       color: "text-red-400",    bg: "bg-red-500/10" },
  musica:        { label: "Música & Audio",          icon: <Music size={16} />,       color: "text-purple-400", bg: "bg-purple-500/10" },
  entretenimiento: { label: "Cine, Series & TV",    icon: <Film size={16} />,        color: "text-blue-400",   bg: "bg-blue-500/10" },
  comida:        { label: "Comida & Bebidas",        icon: <Utensils size={16} />,    color: "text-orange-400", bg: "bg-orange-500/10" },
  juegos:        { label: "Videojuegos",             icon: <Gamepad2 size={16} />,    color: "text-green-400",  bg: "bg-green-500/10" },
  salud:         { label: "Salud & Medicamentos",    icon: <Heart size={16} />,       color: "text-red-400",    bg: "bg-red-500/10" },
  finanzas:      { label: "Finanzas & Crypto",       icon: <DollarSign size={16} />,  color: "text-yellow-400", bg: "bg-yellow-500/10" },
  viajes:        { label: "Viajes & Clima",          icon: <Plane size={16} />,       color: "text-cyan-400",   bg: "bg-cyan-500/10" },
  tecnologia:    { label: "Tecnología",              icon: <Monitor size={16} />,     color: "text-indigo-400", bg: "bg-indigo-500/10" },
  ecommerce:     { label: "E-Commerce",              icon: <ShoppingCart size={16} />, color: "text-teal-400",  bg: "bg-teal-500/10" },
};

// ─── Result Card ──────────────────────────────────────────────────────────────
function ResultCard({ result }) {
  const [imgError, setImgError] = useState(false);
  return (
    <div className="bg-white/5 rounded-lg overflow-hidden flex flex-col">
      {result.image && !imgError ? (
        <img
          src={result.image} alt={result.title}
          className="w-full h-28 object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="w-full h-28 bg-white/5 flex items-center justify-center">
          <Globe size={24} className="text-gray-600" />
        </div>
      )}
      <div className="p-2.5 flex flex-col flex-1">
        <p className="text-white text-xs font-medium line-clamp-2 mb-1">{result.title}</p>
        {result.subtitle && <p className="text-gray-400 text-xs line-clamp-1">{result.subtitle}</p>}
        {result.description && <p className="text-gray-600 text-xs line-clamp-2 mt-1">{result.description}</p>}
        <div className="flex items-center gap-2 mt-auto pt-2">
          {result.url && (
            <a href={result.url} target="_blank" rel="noopener noreferrer"
               className="text-red-400 hover:text-red-300 transition-colors">
              <ExternalLink size={12} />
            </a>
          )}
          {result.extra?.preview && (
            <a href={result.extra.preview} target="_blank" rel="noopener noreferrer"
               className="text-green-400 hover:text-green-300">
              <Play size={12} />
            </a>
          )}
          {result.extra?.rating && (
            <span className="text-yellow-400 text-xs flex items-center gap-0.5">
              <Star size={10} />{result.extra.rating}
            </span>
          )}
          {result.extra?.stars && (
            <span className="text-yellow-400 text-xs flex items-center gap-0.5">
              <Star size={10} />{(result.extra.stars / 1000).toFixed(1)}k
            </span>
          )}
          {result.extra?.price && (
            <span className="text-green-400 text-xs">${result.extra.price}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── API Card ─────────────────────────────────────────────────────────────────
function APICard({ api, catMeta, onSaveKey, onToggle }) {
  const [expanded, setExpanded] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [testError, setTestError] = useState(null);

  const saveKey = async () => {
    if (!apiKey.trim()) return;
    setSaving(true);
    try {
      await onSaveKey(api.id, apiKey.trim());
      setApiKey("");
      toast.success(`Clave para ${api.name} guardada`);
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    if (!query.trim() || testing) return;
    setTesting(true);
    setResults(null);
    setTestError(null);
    try {
      const r = await axios.get(`${API}/admin/knowledge-apis/search/${api.id}`, {
        params: { query: query.trim(), limit: 12 }
      });
      setResults(r.data);
    } catch (e) {
      setTestError(e.response?.data?.detail || "Error al consultar la API");
    } finally { setTesting(false); }
  };

  const statusColor = api.status === "ready"
    ? "text-green-400 bg-green-500/10"
    : api.status === "needs_key"
    ? "text-yellow-400 bg-yellow-500/10"
    : "text-gray-400 bg-gray-500/10";

  const statusLabel = api.status === "ready" ? "Listo" : api.status === "needs_key" ? "Necesita Clave" : "Desactivado";

  return (
    <div className={`glass-panel overflow-hidden transition-all ${!api.enabled ? "opacity-60" : ""}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${catMeta?.bg || "bg-white/10"}`}>
              <span className={catMeta?.color || "text-gray-400"}>{catMeta?.icon || <Globe size={16} />}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-white font-semibold text-sm">{api.name}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor}`}>{statusLabel}</span>
                {!api.requires_key && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 flex items-center gap-1">
                    <Unlock size={10} />Sin clave
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-xs mt-0.5 line-clamp-2">{api.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a href={api.website} target="_blank" rel="noopener noreferrer"
               className="p-1.5 text-gray-500 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
              <ExternalLink size={14} />
            </a>
            <button onClick={() => onToggle(api.id)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
              {api.enabled
                ? <ToggleRight size={20} className="text-green-400" />
                : <ToggleLeft size={20} className="text-gray-500" />}
            </button>
            <button onClick={() => setExpanded(!expanded)}
              className="p-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors">
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-white/10 p-4 space-y-4">
          {/* API Key Input */}
          {api.requires_key && (
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium flex items-center gap-1.5">
                <Key size={12} />API Key {api.has_key ? "(ya configurada)" : "(requerida)"}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type={showKey ? "text" : "password"}
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && saveKey()}
                    placeholder={api.has_key ? "Ingresa nueva clave para actualizar..." : "Ingresa tu API key..."}
                    className="input-glass w-full pr-10 text-sm"
                  />
                  <button onClick={() => setShowKey(!showKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                    {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <button onClick={saveKey} disabled={saving || !apiKey.trim()}
                  className="btn-primary px-4 text-sm flex items-center gap-1">
                  {saving ? <RefreshCw size={12} className="animate-spin" /> : <Check size={12} />}
                  Guardar
                </button>
              </div>
              <p className="text-gray-600 text-xs mt-1">
                Obtén tu clave en{" "}
                <a href={api.website} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:underline">
                  {api.website}
                </a>
              </p>
            </div>
          )}

          {/* Test Search */}
          {(api.status === "ready" || !api.requires_key) && (
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Probar búsqueda</label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && runTest()}
                  placeholder={`Buscar en ${api.name}...`}
                  className="input-glass flex-1 text-sm"
                />
                <button onClick={runTest} disabled={testing || !query.trim()}
                  className="btn-secondary px-4 text-sm flex items-center gap-1">
                  {testing ? <RefreshCw size={12} className="animate-spin" /> : <Search size={12} />}
                  Buscar
                </button>
              </div>

              {testError && (
                <div className="flex items-center gap-2 bg-red-500/10 text-red-400 p-3 rounded-lg text-sm">
                  <AlertCircle size={14} />
                  {testError}
                </div>
              )}

              {results && (
                <div>
                  <p className="text-gray-500 text-xs mb-2">
                    {results.total} resultado{results.total !== 1 ? "s" : ""} · {results.api}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-96 overflow-y-auto">
                    {results.results.map((r, i) => (
                      <ResultCard key={r.id || i} result={r} />
                    ))}
                  </div>
                  {results.results.length === 0 && (
                    <p className="text-gray-600 text-sm text-center py-4">Sin resultados para "{query}"</p>
                  )}
                </div>
              )}
            </div>
          )}

          {api.status === "needs_key" && !results && (
            <div className="flex items-center gap-2 bg-yellow-500/10 text-yellow-400 p-3 rounded-lg text-sm">
              <Key size={14} />
              Ingresa tu API key para probar esta integración
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Feeds Module ─────────────────────────────────────────────────────────────
const FEED_CATEGORY_LABELS = {
  imagenes: { label: "Imágenes", icon: "🖼️" },
  videos: { label: "Videos", icon: "📹" },
  musica: { label: "Música", icon: "🎵" },
  entretenimiento: { label: "Entretenimiento", icon: "🎬" },
  comida: { label: "Comida", icon: "🍽️" },
  juegos: { label: "Juegos", icon: "🎮" },
  salud: { label: "Salud", icon: "🏥" },
  finanzas: { label: "Finanzas", icon: "💰" },
  viajes: { label: "Viajes", icon: "✈️" },
  tecnologia: { label: "Tecnología", icon: "💻" },
  ecommerce: { label: "E-Commerce", icon: "🛒" },
};

function FeedsModule() {
  const [presets, setPresets] = useState({});
  const [customFeeds, setCustomFeeds] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newFeed, setNewFeed] = useState({ name: "", url: "", category: "tecnologia" });
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const loadFeeds = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/feeds/`);
      setPresets(r.data.preset_feeds || {});
      setCustomFeeds(r.data.custom_feeds || []);
      if (!activeCategory) {
        const cats = Object.keys(r.data.preset_feeds || {});
        if (cats.length > 0) setActiveCategory(cats[0]);
      }
    } catch (e) {
      toast.error("Error al cargar feeds");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadFeeds(); }, [loadFeeds]);

  const addFeed = async () => {
    if (!newFeed.name || !newFeed.url) return toast.error("Nombre y URL son requeridos");
    setAdding(true);
    try {
      await axios.post(`${API}/admin/feeds/`, newFeed);
      toast.success("Feed agregado");
      setNewFeed({ name: "", url: "", category: activeCategory || "tecnologia" });
      setShowAddForm(false);
      await loadFeeds();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al agregar feed");
    } finally { setAdding(false); }
  };

  const deleteFeed = async (feedId) => {
    try {
      await axios.delete(`${API}/admin/feeds/${feedId}`);
      toast.success("Feed eliminado");
      await loadFeeds();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const categories = Object.keys(presets);
  const categoryCustomFeeds = customFeeds.filter(f => f.category === activeCategory);

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <RefreshCw size={24} className="text-red-400 animate-spin" />
    </div>
  );

  const totalPresets = Object.values(presets).reduce((s, v) => s + v.length, 0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-white font-semibold text-lg flex items-center gap-2">
            <Globe size={20} className="text-red-400" />Módulo de Feeds
          </h3>
          <p className="text-gray-500 text-sm mt-0.5">
            {totalPresets} URLs preconfiguradas + {customFeeds.length} personalizadas en {categories.length} categorías
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(v => !v)}
          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          <Globe size={14} />{showAddForm ? "Cancelar" : "+ Agregar URL"}
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="glass-panel p-4 mb-6 border border-red-500/20">
          <h4 className="text-white font-medium mb-3 text-sm">Nueva URL de Feed</h4>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <input
              type="text"
              value={newFeed.name}
              onChange={e => setNewFeed(p => ({ ...p, name: e.target.value }))}
              placeholder="Nombre del feed"
              className="input-glass text-sm col-span-1"
            />
            <input
              type="url"
              value={newFeed.url}
              onChange={e => setNewFeed(p => ({ ...p, url: e.target.value }))}
              placeholder="https://..."
              className="input-glass text-sm col-span-1"
            />
            <select
              value={newFeed.category}
              onChange={e => setNewFeed(p => ({ ...p, category: e.target.value }))}
              className="input-glass text-sm col-span-1"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {FEED_CATEGORY_LABELS[cat]?.label || cat}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={addFeed}
            disabled={adding}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm rounded-lg font-medium transition-colors"
          >
            {adding ? "Guardando..." : "Guardar Feed"}
          </button>
        </div>
      )}

      {/* Category Tabs */}
      <div className="flex gap-1 flex-wrap mb-5">
        {categories.map(cat => {
          const meta = FEED_CATEGORY_LABELS[cat];
          const count = (presets[cat]?.length || 0) + customFeeds.filter(f => f.category === cat).length;
          return (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                activeCategory === cat
                  ? "bg-red-500 text-white"
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
              }`}
            >
              <span>{meta?.icon}</span>
              {meta?.label || cat}
              <span className={`px-1.5 py-0.5 rounded text-xs ${activeCategory === cat ? "bg-white/20" : "bg-white/10"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Feed List */}
      {activeCategory && (
        <div className="glass-panel p-4">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg">{FEED_CATEGORY_LABELS[activeCategory]?.icon}</span>
            <h4 className="text-white font-medium">{FEED_CATEGORY_LABELS[activeCategory]?.label || activeCategory}</h4>
            <span className="text-gray-500 text-xs">({(presets[activeCategory]?.length || 0) + categoryCustomFeeds.length} URLs)</span>
          </div>

          {/* Custom feeds */}
          {categoryCustomFeeds.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Personalizadas</p>
              <div className="space-y-1.5">
                {categoryCustomFeeds.map(feed => (
                  <div key={feed.id} className="flex items-center gap-3 p-2.5 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-medium">{feed.name}</p>
                      <p className="text-gray-500 text-xs truncate">{feed.url}</p>
                    </div>
                    <a href={feed.url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors">
                      <ExternalLink size={12} />
                    </a>
                    <button onClick={() => deleteFeed(feed.id)} className="text-gray-600 hover:text-red-400 transition-colors">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preset feeds */}
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Preconfiguradas ({presets[activeCategory]?.length || 0})</p>
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
              {(presets[activeCategory] || []).map((feed, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2.5 bg-white/3 border border-white/8 rounded-lg hover:bg-white/5 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-medium">{feed.name}</p>
                    <p className="text-gray-600 text-xs truncate">{feed.url}</p>
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${feed.type === 'rss' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}`}>
                    {feed.type || 'web'}
                  </span>
                  <a href={feed.url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors">
                    <ExternalLink size={12} />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Deep Web Scraper Sub-component ──────────────────────────────────────────
function DeepScrapePanel() {
  const [url, setUrl] = useState('');
  const [query, setQuery] = useState('');
  const [maxPages, setMaxPages] = useState(15);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [polling, setPolling] = useState(false);
  const pollRef = React.useRef(null);

  const startScrape = async () => {
    if (!url.trim()) { toast.error('Ingresa una URL'); return; }
    try {
      const r = await axios.post(`${API}/knowledge/scrape`, {
        url: url.trim(), query: query.trim() || undefined, max_pages: maxPages, depth: 2,
      });
      setJobId(r.data.job_id);
      setJob({ status: 'starting' });
      setPolling(true);
      toast.success('Scraping iniciado — procesando páginas…');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar scraping');
    }
  };

  useEffect(() => {
    if (!polling || !jobId) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/knowledge/scrape/${jobId}`);
        setJob(r.data);
        if (['done', 'error'].includes(r.data.status)) {
          setPolling(false);
          clearInterval(pollRef.current);
          if (r.data.status === 'done') toast.success('¡Scraping completo!');
          else toast.error('Error durante el scraping');
        }
      } catch { clearInterval(pollRef.current); setPolling(false); }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [polling, jobId]);

  const statusLabel = {
    starting: '⏳ Iniciando…',
    running: `🔍 Procesando ${job?.progress?.pages ?? 0} páginas…`,
    summarizing: '🤖 Generando resumen IA…',
    done: `✅ Completado — ${job?.scrape_result?.pages_scraped ?? 0} páginas`,
    error: `❌ Error: ${job?.error || 'desconocido'}`,
  };

  return (
    <div className="glass-panel p-6 mb-6">
      <h4 className="text-white font-medium mb-1 flex items-center gap-2">
        <Globe className="text-red-400" size={18} />Deep Web Scraping IA
      </h4>
      <p className="text-gray-500 text-xs mb-4">Extrae y resume información de hasta 30 páginas de un sitio web (profundidad 2 niveles)</p>

      <div className="space-y-3">
        <input type="url" value={url} onChange={e => setUrl(e.target.value)}
          placeholder="https://ejemplo.com" className="input-glass w-full" />
        <input type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="¿Qué información buscas? (opcional, mejora el resumen IA)" className="input-glass w-full" />
        <div className="flex items-center gap-3">
          <label className="text-gray-400 text-xs whitespace-nowrap">Máx. páginas:</label>
          <input type="number" min={1} max={30} value={maxPages}
            onChange={e => setMaxPages(Math.min(30, Math.max(1, parseInt(e.target.value) || 15)))}
            className="input-glass w-24" />
          <button onClick={startScrape} disabled={polling || !url.trim()}
            className="btn-primary flex items-center gap-2 flex-1 justify-center">
            {polling ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
            {polling ? 'Procesando…' : 'Iniciar Scraping'}
          </button>
        </div>
      </div>

      {job && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              job.status === 'done' ? 'bg-green-500/20 text-green-400' :
              job.status === 'error' ? 'bg-red-500/20 text-red-400' :
              'bg-yellow-500/20 text-yellow-300'
            }`}>
              {statusLabel[job.status] || job.status}
            </span>
            {job.progress?.current_url && polling && (
              <span className="text-gray-500 text-xs truncate max-w-[200px]" title={job.progress.current_url}>
                {job.progress.current_url}
              </span>
            )}
          </div>

          {job.status === 'running' && (
            <div className="w-full bg-white/5 rounded-full h-1.5 overflow-hidden">
              <div className="bg-red-500 h-full animate-pulse" style={{ width: `${Math.min(99, ((job.progress?.pages || 0) / maxPages) * 100)}%` }} />
            </div>
          )}

          {job.ai_summary && (
            <div className="bg-white rounded-xl p-4">
              <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider mb-2">Resumen IA</p>
              <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{job.ai_summary}</div>
              {job.scrape_result?.top_links?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider mb-1.5">Páginas rastreadas</p>
                  <div className="flex flex-wrap gap-1.5">
                    {job.scrape_result.top_links.slice(0, 6).map((l, i) => (
                      <a key={i} href={l} target="_blank" rel="noopener noreferrer"
                        className="text-[10px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full hover:bg-blue-100 truncate max-w-[160px]" title={l}>
                        {new URL(l).pathname || l}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Fuentes de Conocimiento Sub-section ──────────────────────────────────────
function KnowledgeSourcesSection() {
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [newUrl, setNewUrl] = useState({ name: "", url: "" });

  const loadSources = useCallback(async () => {
    setLoadingSources(true);
    try {
      const r = await axios.get(`${API}/knowledge-sources`);
      setSources(r.data || []);
    } catch {
      toast.error("Error al cargar fuentes de conocimiento");
    } finally { setLoadingSources(false); }
  }, []);

  useEffect(() => { loadSources(); }, [loadSources]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/knowledge-sources/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Documento subido correctamente");
      loadSources();
    } catch { toast.error("Error al subir documento"); }
    finally { setUploadingFile(false); }
  };

  const handleAddUrl = async () => {
    if (!newUrl.url || !newUrl.name) return;
    try {
      await axios.post(`${API}/knowledge-sources/url?url=${encodeURIComponent(newUrl.url)}&name=${encodeURIComponent(newUrl.name)}`);
      toast.success("URL agregada correctamente");
      setNewUrl({ name: "", url: "" });
      loadSources();
    } catch { toast.error("Error al agregar URL"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/knowledge-sources/${id}`);
      toast.success("Fuente eliminada");
      loadSources();
    } catch { toast.error("Error al eliminar fuente"); }
  };

  const handleToggle = async (id) => {
    try {
      await axios.put(`${API}/knowledge-sources/${id}/toggle`);
      loadSources();
    } catch { toast.error("Error al cambiar estado"); }
  };

  return (
    <div>
      <div className="flex justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Brain size={20} className="text-red-400" />Fuentes de Conocimiento
          </h3>
          <p className="text-gray-500 text-sm mt-1">Sube documentos y URLs para enriquecer las respuestas del chatbot</p>
        </div>
        <button onClick={loadSources} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg">
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="glass-panel p-6">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <Upload className="text-red-400" size={18} />Subir Documento
          </h4>
          <div className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center hover:border-red-500/50 transition-colors">
            <input type="file" id="knowledge-file-upload" className="hidden"
              onChange={handleFileUpload} accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt" />
            <label htmlFor="knowledge-file-upload" className="cursor-pointer">
              <Upload className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-2">Arrastra o haz clic para subir</p>
              <p className="text-gray-600 text-sm">PDF, Word, Excel, PowerPoint, TXT</p>
              {uploadingFile && <p className="text-red-400 mt-2 text-sm">Subiendo...</p>}
            </label>
          </div>
        </div>

        <div className="glass-panel p-6">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <Link className="text-red-400" size={18} />Agregar URL
          </h4>
          <input type="text" value={newUrl.name}
            onChange={(e) => setNewUrl({ ...newUrl, name: e.target.value })}
            placeholder="Nombre de la fuente" className="input-glass w-full mb-3" />
          <input type="url" value={newUrl.url}
            onChange={(e) => setNewUrl({ ...newUrl, url: e.target.value })}
            placeholder="https://ejemplo.com/pagina" className="input-glass w-full mb-4" />
          <button onClick={handleAddUrl} disabled={!newUrl.url || !newUrl.name}
            className="btn-primary w-full flex items-center justify-center gap-2">
            <Plus size={16} />Agregar URL
          </button>
        </div>
      </div>

      <DeepScrapePanel />

      <div className="glass-panel p-6">
        <h4 className="text-white font-medium mb-4">Fuentes Activas ({sources.length})</h4>
        {loadingSources ? (
          <div className="flex justify-center py-8"><RefreshCw size={20} className="text-red-400 animate-spin" /></div>
        ) : sources.length === 0 ? (
          <div className="text-center py-8">
            <Brain className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500">No hay fuentes de conocimiento</p>
            <p className="text-gray-600 text-sm mt-2">Sube documentos o agrega URLs para mejorar las respuestas</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sources.map(source => (
              <div key={source.id} className="p-4 bg-black/20 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${source.source_type === 'url' ? 'bg-blue-500/20' : 'bg-red-500/20'}`}>
                    {source.source_type === 'url'
                      ? <Globe className="w-5 h-5 text-blue-400" />
                      : <FileText className="w-5 h-5 text-red-400" />}
                  </div>
                  <div>
                    <p className="text-white font-medium">{source.name}</p>
                    <p className="text-gray-500 text-sm">
                      {source.source_type === 'url'
                        ? (source.url?.substring(0, 40) + (source.url?.length > 40 ? '...' : ''))
                        : source.file_type?.toUpperCase()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => handleToggle(source.id)}
                    className={`text-xs px-2 py-1 rounded-full font-medium cursor-pointer ${source.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {source.is_active ? 'Activo' : 'Inactivo'}
                  </button>
                  <button onClick={() => handleDelete(source.id)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function KnowledgeAPIsManager() {
  const [activeMainTab, setActiveMainTab] = useState("apis");
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchFilter, setSearchFilter] = useState("");

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/knowledge-apis/catalog`);
      setCatalog(r.data);
      setLoadError(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setLoadError(detail?.message || detail || "Error al cargar el catálogo de APIs");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const handleSaveKey = async (apiId, apiKey) => {
    await axios.post(`${API}/admin/knowledge-apis/${apiId}/configure`, { api_key: apiKey });
    await loadCatalog();
  };

  const handleToggle = async (apiId) => {
    await axios.put(`${API}/admin/knowledge-apis/${apiId}/toggle`);
    await loadCatalog();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw size={24} className="text-red-400 animate-spin" />
      </div>
    );
  }

  if (loadError && !catalog) {
    return (
      <div className="glass-panel p-8 text-center">
        <AlertTriangle size={24} className="text-red-400 mx-auto mb-3" />
        <p className="text-red-200 text-sm">{loadError}</p>
        <button onClick={loadCatalog} className="btn-secondary mt-4 text-sm">Reintentar</button>
      </div>
    );
  }

  const apis = catalog?.apis || [];
  const categories = [...new Set(apis.map(a => a.category))];

  const filtered = apis.filter(api => {
    const matchesCategory = activeCategory === "all" || api.category === activeCategory;
    const matchesSearch = !searchFilter || 
      api.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
      api.description.toLowerCase().includes(searchFilter.toLowerCase()) ||
      api.tags?.some(t => t.toLowerCase().includes(searchFilter.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const groupedFiltered = categories.reduce((acc, cat) => {
    const items = filtered.filter(a => a.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  return (
    <div>
      {/* Main Tab switcher */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-2">
        {[
          { id: "apis", label: "APIs de Conocimiento", icon: <Globe size={14} /> },
          { id: "feeds", label: "Módulo de Feeds", icon: <Activity size={14} /> },
          { id: "sources", label: "Fuentes de Conocimiento", icon: <Brain size={14} /> },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveMainTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeMainTab === tab.id
                ? "bg-red-500 text-white"
                : "text-gray-400 hover:text-white hover:bg-white/10"
            }`}
          >
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {activeMainTab === "feeds" && <FeedsModule />}
      {activeMainTab === "sources" && <KnowledgeSourcesSection />}

      {activeMainTab === "apis" && <>
      {/* Header + Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-white font-semibold text-lg flex items-center gap-2">
            <Globe size={20} className="text-red-400" />APIs de Conocimiento
          </h3>
          <p className="text-gray-500 text-sm mt-0.5">
            Conectores de datos libres y gratuitos para enriquecer las respuestas de tus agentes IA
          </p>
        </div>
        <button onClick={loadCatalog} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg">
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total APIs", value: catalog?.total || 0, color: "text-white" },
          { label: "Listas para usar", value: catalog?.ready || 0, color: "text-green-400" },
          { label: "Necesitan clave", value: catalog?.needs_key || 0, color: "text-yellow-400" },
        ].map(s => (
          <div key={s.label} className="glass-panel p-4 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-gray-500 text-xs mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search + Category Filter */}
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            placeholder="Buscar APIs por nombre o categoría..."
            className="input-glass w-full pl-9 text-sm"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => setActiveCategory("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeCategory === "all" ? "bg-red-500 text-white" : "bg-white/5 text-gray-400 hover:bg-white/10"
            }`}
          >
            Todas
          </button>
          {categories.map(cat => {
            const meta = CATEGORY_META[cat];
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                  activeCategory === cat
                    ? "bg-red-500 text-white"
                    : `${meta?.bg || "bg-white/5"} ${meta?.color || "text-gray-400"} hover:opacity-80`
                }`}
              >
                {meta?.icon}{meta?.label || cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* APIs by Category */}
      <div className="space-y-8">
        {Object.entries(groupedFiltered).map(([cat, catApis]) => {
          const meta = CATEGORY_META[cat];
          return (
            <div key={cat}>
              <div className={`flex items-center gap-2 mb-3 px-1`}>
                <span className={meta?.color || "text-gray-400"}>{meta?.icon}</span>
                <h4 className={`font-semibold text-sm ${meta?.color || "text-gray-300"}`}>
                  {meta?.label || cat}
                </h4>
                <span className="text-gray-600 text-xs">({catApis.length})</span>
              </div>
              <div className="space-y-3">
                {catApis.map(api => (
                  <APICard
                    key={api.id}
                    api={api}
                    catMeta={CATEGORY_META[api.category]}
                    onSaveKey={handleSaveKey}
                    onToggle={handleToggle}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="glass-panel p-12 text-center">
            <Globe size={32} className="text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No se encontraron APIs</p>
            <p className="text-gray-600 text-sm mt-1">Prueba con otro filtro o búsqueda</p>
          </div>
        )}
      </div>

      {/* Footer note */}
      <div className="mt-6 p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl">
        <p className="text-blue-400 text-sm font-medium mb-1">Uso en Agentes IA</p>
        <p className="text-gray-500 text-xs">
          Las APIs activadas aquí estarán disponibles como fuentes de conocimiento para los agentes IA. 
          Las APIs sin clave están listas para usar inmediatamente. Las APIs marcadas en amarillo requieren 
          registro gratuito en sus respectivos sitios web para obtener una API key.
        </p>
      </div>
      </>}
    </div>
  );
}
